import { useEffect, useMemo, useState } from "react";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Textarea from "#/components/shared/textarea";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { formatDate } from "#/components/utls/time";
import useArticleOutlineDetail from "#/admin/article-outlines/hooks/useArticleOutlineDetail";
import useArticleOutlineMutations from "#/admin/article-outlines/hooks/useArticleOutlineMutations";

type OutlineFormState = {
  title: string;
  outline: string;
};

const emptyForm: OutlineFormState = {
  title: "",
  outline: "",
};

export function ArticleOutlineDetail({ id }: { id: string }) {
  const detailQuery = useArticleOutlineDetail(id);
  const { updateOutline } = useArticleOutlineMutations();
  const [formState, setFormState] = useState<OutlineFormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!detailQuery.data) return;
    const outlineText =
      detailQuery.data.outline ??
      detailQuery.data.content ??
      detailQuery.data.body ??
      "";
    setFormState({
      title: detailQuery.data.title ?? detailQuery.data.name ?? "",
      outline: outlineText ?? "",
    });
  }, [detailQuery.data]);

  const updatedLabel = useMemo(() => {
    const value =
      detailQuery.data?.updated_at || detailQuery.data?.created_at || "";
    return value ? formatDate(value) : "Unknown";
  }, [detailQuery.data?.created_at, detailQuery.data?.updated_at]);

  const handleChange = (field: keyof OutlineFormState, value: string) => {
    setFormState((current) => ({ ...current, [field]: value }));
    setError(null);
  };

  const handleSave = async () => {
    const trimmedOutline = formState.outline.trim();
    if (!trimmedOutline) {
      setError("Outline content is required.");
      return;
    }

    await updateOutline.mutateAsync({
      id,
      payload: {
        title: formState.title.trim() || undefined,
        outline: trimmedOutline,
      },
    });
  };

  const rawOutline =
    detailQuery.data?.outline ??
    detailQuery.data?.content ??
    detailQuery.data?.body ??
    "";
  const bullets = detailQuery.data?.bullets ?? [];
  const isApproved = Boolean(detailQuery.data?.approved_at);

  return (
    <div className="flex flex-col gap-6">
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <div className="flex flex-col gap-1">
            <Typography variant="h3" className="text-brown-900">
              {detailQuery.data?.title ||
                detailQuery.data?.name ||
                "Article outline"}
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Last updated: {updatedLabel}
            </Typography>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                isApproved
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {isApproved ? "Approved" : "Pending approval"}
            </span>
          </div>
        </Card.Header>
        <Card.Body className="space-y-4 bg-white">
          {detailQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading outline…
            </Typography>
          ) : detailQuery.isError ? (
            <Typography variant="body2" className="text-terracotta">
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : "Unable to load outline."}
            </Typography>
          ) : (
            <>
              {Array.isArray(bullets) && bullets.length > 0 ? (
                <div className="space-y-3">
                  <Typography variant="subtitle2" className="text-brown-700">
                    Bullets
                  </Typography>
                  <ul className="list-disc space-y-2 pl-6 text-sm text-brown-700">
                    {bullets.map((bullet, index) => (
                      <li key={`${bullet}-${index}`}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="space-y-3">
                <Typography variant="subtitle2" className="text-brown-700">
                  Full outline
                </Typography>
                <pre className="whitespace-pre-wrap rounded-lg border border-beige-200 bg-cream-50 p-4 text-sm text-brown-700">
                  {rawOutline || "No outline content provided."}
                </pre>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="w-full" variant="cream" elevation={1}>
        <Card.Header variant="cream" divider className="bg-cream-100">
          <Typography variant="h4" className="text-brown-900">
            Update outline
          </Typography>
        </Card.Header>
        <Card.Body className="space-y-4 bg-white">
          <Input
            id="outline-detail-title"
            label="Title (optional)"
            placeholder="Outline title"
            value={formState.title}
            onChange={(event) => handleChange("title", event.target.value)}
            fullWidth
          />
          <Textarea
            id="outline-detail-body"
            label="Outline"
            placeholder="Update the outline content"
            value={formState.outline}
            onChange={(event) => handleChange("outline", event.target.value)}
            rows={8}
            fullWidth
          />
          {error ? (
            <Typography variant="body2" className="text-terracotta">
              {error}
            </Typography>
          ) : null}
        </Card.Body>
        <Card.Footer className="bg-white">
          <ActionButton
            onClick={handleSave}
            isLoading={updateOutline.isPending}
            disabled={detailQuery.isLoading || updateOutline.isPending}
          >
            Save changes
          </ActionButton>
        </Card.Footer>
      </Card>
    </div>
  );
}

export default ArticleOutlineDetail;
